import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  concatMap,
  debounceTime,
  filter,
  map,
  of,
  startWith,
  switchMap,
} from 'rxjs';

import { EffectsBase, withCacheGate } from '@ps/core-data-access';
import { CACHE_TTL, isCacheStale, markCached } from '@ps/utils';
import { mapSurveyLocalization } from '@ps/survey-utils';

import { QualificationsService } from '../../services/qualifications/qualifications.service';
import { SurveyQuestionService } from '../../services/survey-question/survey-question.service';
import { LocationConditionsService } from '../../services/location-conditions/location-conditions.service';
import {
  isSurveyQuestionsCacheValid,
  locationConditionsCacheKey,
  surveyQuestionsCacheKey,
} from '../../utils';
import {
  SurveyCreationReferenceActions,
  SurveyCreationReferenceApiActions,
} from './survey-creation-reference.actions';
import {
  selectLocationConditionsRecord,
  selectQualificationsRecord,
  selectQuestionCategoriesCachedAt,
  selectQuestionsRecord,
} from './survey-creation-reference.selectors';

const QUALIFICATIONS_CACHE_TTL_MS = CACHE_TTL.DEFAULT;
const LOCATION_CONDITIONS_CACHE_TTL_MS = CACHE_TTL.TWO_HOUR;

@Injectable()
export class SurveyCreationReferenceEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _qualificationsService = inject(QualificationsService);
  private readonly _questionService = inject(SurveyQuestionService);
  private readonly _locationConditionsService = inject(
    LocationConditionsService,
  );

  public readonly loadQualifications$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationReferenceActions.loadQualifications),
      concatLatestFrom(() => this._store.select(selectQualificationsRecord)),
      filter(([{ countryCode, languageShortCode }, qualificationsRecord]) => {
        const localeKey = mapSurveyLocalization(countryCode, languageShortCode);
        const entry = qualificationsRecord[localeKey];
        return !(
          entry &&
          entry.data.length > 0 &&
          !isCacheStale(entry.cachedAt, QUALIFICATIONS_CACHE_TTL_MS)
        );
      }),
      concatMap(([{ countryCode, languageShortCode }]) =>
        this._qualificationsService
          .getQualifications(countryCode, languageShortCode)
          .pipe(
            map((qualifications) =>
              SurveyCreationReferenceApiActions.loadQualificationsSuccess({
                countryCode,
                languageShortCode,
                qualifications,
                cachedAt: markCached(),
              }),
            ),
            this.psCatchError((err) =>
              of(
                SurveyCreationReferenceApiActions.loadQualificationsFailure({
                  countryCode,
                  languageShortCode,
                  error: err.message || 'Failed to load qualifications',
                }),
              ),
            ),
          ),
      ),
    ),
  );

  public readonly loadQuestionCategories$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationReferenceActions.loadQuestionCategories),
      withCacheGate(
        this._store.select(selectQuestionCategoriesCachedAt),
        CACHE_TTL.DEFAULT,
      ),
      switchMap(() =>
        this._questionService.categories().pipe(
          map((categories) =>
            SurveyCreationReferenceApiActions.loadQuestionCategoriesSuccess({
              categories,
              cachedAt: markCached(),
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                SurveyCreationReferenceApiActions.loadQuestionCategoriesFailure(
                  {
                    error: err.message || 'Failed to load question categories',
                  },
                ),
              ),
            {
              notify: true,
              customMessage:
                'An error occurred while loading categories. Please try again.',
            },
          ),
        ),
      ),
    ),
  );

  /**
   * Debounced ahead of the gate so a cached query costs nothing and no spinner
   * flashes. `switchMap` (not `concatMap`) — the newest search wins and
   * superseded requests cancel.
   */
  public readonly loadQuestions$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationReferenceActions.loadQuestions),
      debounceTime(500),
      concatLatestFrom(() => this._store.select(selectQuestionsRecord)),
      filter(
        ([{ search, categoryIds }, questionsRecord]) =>
          !isSurveyQuestionsCacheValid(
            surveyQuestionsCacheKey(search, categoryIds),
            questionsRecord,
          ),
      ),
      switchMap(([{ search, categoryIds }]) => {
        const key = surveyQuestionsCacheKey(search, categoryIds);

        return this._questionService.getQuestions(search, categoryIds).pipe(
          map((questions) =>
            SurveyCreationReferenceApiActions.loadQuestionsSuccess({
              key,
              questions,
              cachedAt: markCached(),
            }),
          ),
          this.psCatchError((err) =>
            of(
              SurveyCreationReferenceApiActions.loadQuestionsFailure({
                key,
                error: err.message || 'Failed to load questions',
              }),
            ),
          ),
          startWith(
            SurveyCreationReferenceApiActions.loadQuestionsStarted({ key }),
          ),
        );
      }),
    ),
  );

  /**
   * Loads the full option list for a location qualification once per
   * (dbQualificationCode, locale) and caches it for
   * CACHE_TTL.TWO_HOUR — location values (CBSA/CSA/County/DMA names) are
   * static reference data, so there's no reason to keep re-fetching within a
   * session. Unlike the old per-keystroke search this superseded, the buyer's
   * typed text never reaches this effect (or the server) at all — it's used
   * purely for client-side filtering (see
   * SearchLocationQualificationContentComponent) against whatever this
   * effect loads. The cache gate treats a fresh empty result as valid too
   * (mirrors isSurveyQuestionsCacheValid — "no options" is itself a complete
   * answer, not a reason to keep asking). `switchMap` still cancels an
   * overlapping in-flight load (e.g. rapid tab switching).
   */
  public readonly loadLocationConditions$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationReferenceActions.loadLocationConditions),
      concatLatestFrom(() =>
        this._store.select(selectLocationConditionsRecord),
      ),
      filter(([{ dbQualificationCode, locale }, locationConditionsRecord]) => {
        const key = locationConditionsCacheKey(dbQualificationCode, locale);
        const entry = locationConditionsRecord[key];
        return !(
          entry &&
          !isCacheStale(entry.cachedAt, LOCATION_CONDITIONS_CACHE_TTL_MS)
        );
      }),
      switchMap(([{ dbQualificationCode, locale }]) => {
        const key = locationConditionsCacheKey(dbQualificationCode, locale);

        return this._locationConditionsService
          .getAll(dbQualificationCode, locale)
          .pipe(
            map((conditions) =>
              SurveyCreationReferenceApiActions.loadLocationConditionsSuccess({
                key,
                conditions,
                cachedAt: markCached(),
              }),
            ),
            this.psCatchError(
              (err) =>
                of(
                  SurveyCreationReferenceApiActions.loadLocationConditionsFailure(
                    {
                      key,
                      error:
                        err.message || 'Failed to load location conditions',
                    },
                  ),
                ),
              {
                notify: true,
                customMessage:
                  'An error occurred while loading options. Please try again.',
              },
            ),
            startWith(
              SurveyCreationReferenceApiActions.loadLocationConditionsStarted({
                key,
              }),
            ),
          );
      }),
    ),
  );
}
